/**
 * The Web Audio side of the white noise generator.
 *
 * Rules the player keeps, because each one is a way a noise app goes wrong:
 *
 * - **Nothing plays by itself.** The context is created inside `start()`, which the
 *   widget calls from a button press, so browsers' autoplay policies are satisfied and
 *   the page is silent until the reader asks.
 * - **No clicks.** A gain that jumps makes a pop, so every level change is a ramp: a
 *   quarter-second fade in, a short fade out before stopping, a smoothed volume.
 * - **Nothing is downloaded.** The noise is synthesised in the browser (`noise.ts`) and
 *   played from a looping buffer.
 *
 * Signal path: looping buffer → fade gain → low-pass ("softness") → volume gain → output.
 */

import {
  FADE_OUT_SECONDS,
  makeNoise,
  type NoiseKind,
  softnessToCutoff,
} from "@/lib/tool-kit/study-more/noise";

const BUFFER_SECONDS = 6;
const FADE_IN_SECONDS = 0.25;
const FADE_OUT_STOP_SECONDS = 0.2;
const SWITCH_FADE_SECONDS = 0.12;
const VOLUME_SMOOTHING = 0.04;

/** Perceptual volume: the slider position squared, so the low end has fine steps. */
export function volumeToGain(volume: number): number {
  const clamped = Math.min(1, Math.max(0, volume));
  return clamped * clamped * 0.8;
}

export interface NoisePlayerOptions {
  /** Called when the timer ends the sound. */
  onEnded: () => void;
  /** Overridable for tests. */
  createContext?: () => AudioContext;
  random?: () => number;
}

interface Graph {
  context: AudioContext;
  fade: GainNode;
  filter: BiquadFilterNode;
  volume: GainNode;
  source: AudioBufferSourceNode | null;
}

/** Runs a promise-returning audio call and ignores a rejection (the context may be closed). */
function quietly(result: Promise<void>): void {
  result.catch(() => undefined);
}

function defaultContext(): AudioContext {
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext: typeof AudioContext })
      .webkitAudioContext;
  return new Ctor();
}

export class NoisePlayer {
  private graph: Graph | null = null;
  private readonly data = new Map<NoiseKind, Float32Array>();
  private kind: NoiseKind = "white";
  private active = false;
  private generation = 0;
  private endTimer: ReturnType<typeof setTimeout> | null = null;
  private swapTimer: ReturnType<typeof setTimeout> | null = null;
  private stopTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly options: NoisePlayerOptions;

  constructor(options: NoisePlayerOptions) {
    this.options = options;
  }

  get playing(): boolean {
    return this.active;
  }

  private ensureGraph(): Graph {
    if (this.graph) {
      return this.graph;
    }
    const context = (this.options.createContext ?? defaultContext)();
    const fade = context.createGain();
    const filter = context.createBiquadFilter();
    const volume = context.createGain();
    filter.type = "lowpass";
    filter.Q.value = Math.SQRT1_2;
    fade.connect(filter);
    filter.connect(volume);
    volume.connect(context.destination);
    this.graph = { context, fade, filter, volume, source: null };
    return this.graph;
  }

  private buffer(graph: Graph, kind: NoiseKind): AudioBuffer {
    const { context } = graph;
    const length = Math.round(context.sampleRate * BUFFER_SECONDS);
    let samples = this.data.get(kind);
    if (!samples || samples.length !== length) {
      samples = makeNoise(kind, length, this.options.random ?? Math.random);
      this.data.set(kind, samples);
    }
    const buffer = context.createBuffer(1, length, context.sampleRate);
    buffer.getChannelData(0).set(samples);
    return buffer;
  }

  private startSource(graph: Graph, kind: NoiseKind): void {
    const source = graph.context.createBufferSource();
    source.buffer = this.buffer(graph, kind);
    source.loop = true;
    source.connect(graph.fade);
    source.start();
    graph.source = source;
  }

  private dropSource(graph: Graph): void {
    graph.source?.stop();
    graph.source?.disconnect();
    graph.source = null;
  }

  private clearTimers(): void {
    for (const timer of [this.endTimer, this.swapTimer, this.stopTimer]) {
      if (timer) {
        clearTimeout(timer);
      }
    }
    this.endTimer = null;
    this.swapTimer = null;
    this.stopTimer = null;
  }

  /** Begins playing. Call from a click: it creates and resumes the audio context. */
  async start(
    kind: NoiseKind,
    volume: number,
    softness: number
  ): Promise<void> {
    const graph = this.ensureGraph();
    this.generation += 1;
    this.clearTimers();
    this.kind = kind;
    const { context } = graph;
    if (context.state === "suspended") {
      await context.resume();
    }
    const now = context.currentTime;
    graph.volume.gain.cancelScheduledValues(now);
    graph.volume.gain.setValueAtTime(volumeToGain(volume), now);
    graph.filter.frequency.setValueAtTime(softnessToCutoff(softness), now);
    this.dropSource(graph);
    this.startSource(graph, kind);
    graph.fade.gain.cancelScheduledValues(now);
    graph.fade.gain.setValueAtTime(0, now);
    graph.fade.gain.linearRampToValueAtTime(1, now + FADE_IN_SECONDS);
    this.active = true;
  }

  setVolume(volume: number): void {
    if (!this.graph) {
      return;
    }
    const { context } = this.graph;
    this.graph.volume.gain.setTargetAtTime(
      volumeToGain(volume),
      context.currentTime,
      VOLUME_SMOOTHING
    );
  }

  setSoftness(softness: number): void {
    if (!this.graph) {
      return;
    }
    const { context } = this.graph;
    this.graph.filter.frequency.setTargetAtTime(
      softnessToCutoff(softness),
      context.currentTime,
      VOLUME_SMOOTHING
    );
  }

  /** Switches colour with a short fade out and in, so there is no click. */
  setKind(kind: NoiseKind): void {
    this.kind = kind;
    const graph = this.graph;
    if (!(graph && this.active)) {
      return;
    }
    this.generation += 1;
    const ticket = this.generation;
    const now = graph.context.currentTime;
    graph.fade.gain.cancelScheduledValues(now);
    graph.fade.gain.setValueAtTime(graph.fade.gain.value, now);
    graph.fade.gain.linearRampToValueAtTime(0, now + SWITCH_FADE_SECONDS);
    if (this.swapTimer) {
      clearTimeout(this.swapTimer);
    }
    this.swapTimer = setTimeout(
      () => {
        if (ticket !== this.generation || !this.active) {
          return;
        }
        const at = graph.context.currentTime;
        this.dropSource(graph);
        this.startSource(graph, this.kind);
        graph.fade.gain.cancelScheduledValues(at);
        graph.fade.gain.setValueAtTime(0, at);
        graph.fade.gain.linearRampToValueAtTime(1, at + FADE_IN_SECONDS);
      },
      SWITCH_FADE_SECONDS * 1000 + 30
    );
  }

  /**
   * Ends the sound after `seconds`, fading it out over the last few seconds. Pass null
   * to cancel a timer and bring the sound back to full level.
   */
  setTimer(seconds: number | null): void {
    const graph = this.graph;
    if (!(graph && this.active)) {
      return;
    }
    if (this.endTimer) {
      clearTimeout(this.endTimer);
      this.endTimer = null;
    }
    const now = graph.context.currentTime;
    graph.fade.gain.cancelScheduledValues(now);
    graph.fade.gain.setValueAtTime(graph.fade.gain.value, now);
    graph.fade.gain.linearRampToValueAtTime(1, now + 0.3);
    if (seconds === null) {
      return;
    }
    const endAt = now + seconds;
    const fadeStart = Math.max(now + 0.3, endAt - FADE_OUT_SECONDS);
    graph.fade.gain.setValueAtTime(1, fadeStart);
    graph.fade.gain.linearRampToValueAtTime(0, endAt);
    this.endTimer = setTimeout(
      () => {
        this.finish();
        this.options.onEnded();
      },
      seconds * 1000 + 50
    );
  }

  private finish(): void {
    const graph = this.graph;
    this.active = false;
    this.generation += 1;
    this.clearTimers();
    if (graph) {
      this.dropSource(graph);
      graph.fade.gain.cancelScheduledValues(graph.context.currentTime);
      graph.fade.gain.value = 0;
      quietly(graph.context.suspend());
    }
  }

  /** Fades out over a fifth of a second, then stops and releases the audio device. */
  stop(): void {
    const graph = this.graph;
    if (!(graph && this.active)) {
      return;
    }
    this.active = false;
    this.generation += 1;
    this.clearTimers();
    const now = graph.context.currentTime;
    graph.fade.gain.cancelScheduledValues(now);
    graph.fade.gain.setValueAtTime(graph.fade.gain.value, now);
    graph.fade.gain.linearRampToValueAtTime(0, now + FADE_OUT_STOP_SECONDS);
    this.stopTimer = setTimeout(
      () => {
        if (!this.active) {
          this.dropSource(graph);
          quietly(graph.context.suspend());
        }
      },
      FADE_OUT_STOP_SECONDS * 1000 + 40
    );
  }

  /** Stops everything and closes the audio context (on unmount). */
  dispose(): void {
    this.clearTimers();
    this.active = false;
    this.generation += 1;
    if (this.graph) {
      this.dropSource(this.graph);
      quietly(this.graph.context.close());
      this.graph = null;
    }
  }
}
